import { yupResolver } from '@hookform/resolvers/yup';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
  DataGridColumnSettings,
  DropDownResult,
  JsonData,
  ModalDialogButtonSetting,
  RadioGroupResult,
} from '@myosh/odin-components';
import {
  Dialog,
  DynamicFormRequiredType,
  HfCheckbox,
  HfInput,
  HfRadioGroup,
  isDropDownNoSelection,
} from '@myosh/odin-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import type { ViewColumn, ViewConfig, ViewConfiguration } from '../../../../@types/views';
import useViewConfigs from '../../../../hooks/use-view-configs';
import { useAppSelector } from '../../../../redux/hooks';
import {
  useAddViewConfigMutation,
  useGetViewColumnsQuery,
  useUpdateViewConfigMutation,
} from '../../../../redux/services/view';
import { dynamicPageState } from '../../../../redux/slices/dynamic-page-slice';
import { showError } from '../../../../services/notification.service';
import { defaultLastEditedFilterOption, defaultRecordFilterOption } from '../../../../utils/dynamic-page-utils';
import { forceAssert } from '../../../../utils/type-guards';
import HfDropDown from '../../../fields/custom-drop-downs/hf-drop-down.component';
import useCanManageGlobalViews from '../hooks/use-can-manage-global-views';
import {
  archivedType,
  CREATE_NEW_VIEW,
  getCreateUpdateOptions,
  lastEditedType,
  mapViewColumn,
  SAVE_EXISTING_VIEW,
} from './save-view-utils';

interface SaveViewModalProps {
  getColumns: () => Array<DataGridColumnSettings>;
  moduleId: number;
  selectedForm: DropDownResult;
  visible: boolean;
  onCloseModal: () => void;
  getFilters: () => ContinuousScrollerFilter[];
  getSortedFields: () => ContinuousScrollerSort[];
  isAllForms: boolean;
}

const FORM_ID = 'saveViewModal';

export default function SaveViewModal({
  getColumns,
  moduleId,
  selectedForm,
  visible,
  onCloseModal,
  getFilters,
  getSortedFields,
  isAllForms,
}: SaveViewModalProps) {
  const [columns, setColumns] = useState<Array<ViewColumn>>([]);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);

  const {
    selectedFilters: { lastEdited = defaultLastEditedFilterOption, records = defaultRecordFilterOption, viewConfig },
  } = useAppSelector(dynamicPageState);

  const { data: viewConfigs, isLoading: isViewConfigsLoading } = useViewConfigs(
    selectedForm && !isDropDownNoSelection(selectedForm) ? (selectedForm?.value as number) : undefined,
    true,
    moduleId,
    isAllForms
  );

  const { canManageGlobalViews } = useCanManageGlobalViews(moduleId, selectedForm.value as number, isAllForms);

  const {
    data: viewColumns,
    isLoading: isViewColumnsLoading,
    isFetching: isViewColumnsFetching,
  } = useGetViewColumnsQuery(
    { configId: viewConfig?.value as number },
    {
      skip: !viewConfig?.value,
    }
  );
  const [addViewConfig] = useAddViewConfigMutation();
  const [updateViewConfig] = useUpdateViewConfigMutation();

  useEffect(() => {
    if (viewColumns && !isViewColumnsFetching && !isViewColumnsLoading) {
      setColumns(viewColumns);
    }
  }, [viewColumns, isViewColumnsFetching, isViewColumnsLoading]);

  /**
   *
   * @param viewId id of a view which will be updated. Underfined, if a new view is to be created
   * @param name a new name for the view if id is undefined
   * @param isGlobal whether the view is global or not
   * @returns updated or new view configuration object
   */
  const getViewConfiguration = (viewId: number | undefined, name: string, isGlobal: boolean) => {
    const filteredColumns = getColumns()
      .filter((dataGridColumn) => (dataGridColumn.id as number) > 0 && dataGridColumn.visible)
      .filter((dataGridColumn) => columns.findIndex((viewColumn) => viewColumn.id === dataGridColumn.id) > -1)
      .map((dataGridColumn, index) => {
        // column config from the API
        const view = columns.find((viewColumn) => viewColumn.id === dataGridColumn.id) as Partial<ViewColumn>;

        const columnHasFilterSelected = getFilters().find(
          (filter) => filter.field === view.entityReference?.entityPropertyName
        );

        const columnHasSortedFilter = getSortedFields().find(
          (sortedfield) => sortedfield.field === view.entityReference?.entityPropertyName
        );

        return mapViewColumn(isAllForms, view, dataGridColumn, index, columnHasFilterSelected, columnHasSortedFilter);
      });

    const configuration: Partial<ViewConfiguration> = {
      id: viewId,
      moduleFormId: isAllForms ? undefined : (selectedForm?.value as number),
      moduleId: moduleId,
      allForms: isAllForms,
      defaultConfig: false,
      global: isGlobal,
      archivedType: archivedType[records.value as string],
      dateRange: lastEditedType[lastEdited.value as string],
      name: name,
      columnConfigurations: filteredColumns,
    };

    return configuration;
  };

  const availableViews = useMemo(() => {
    let allowedViews: Array<ViewConfig> = [];
    if (viewConfigs && !isViewConfigsLoading) {
      allowedViews = viewConfigs.filter((view) => !view.defaultConfig);
      if (canManageGlobalViews === false) {
        allowedViews = allowedViews.filter((view) => view.global === false);
      }
    }
    return allowedViews;
  }, [viewConfigs, canManageGlobalViews, isViewConfigsLoading]);

  const [saveButton, setSaveButton] = useState<ModalDialogButtonSetting>({
    name: 'formSubmitButton',
    text: t('save', 'Save'),
    type: 'primary',
    variant: 'alternative',
    form: FORM_ID,
    disabled: true,
  });

  const formSchema: yup.ObjectSchema<FieldValues> = useMemo(
    () =>
      yup.object().shape({
        saveOption: yup.object().required(),
        newViewName: yup.string().when('saveOption', {
          is: (val: RadioGroupResult) => val.value === CREATE_NEW_VIEW,
          then: (schema) =>
            schema
              .required(t('insert-value', 'Insert Value'))
              .test('not-default', t('view-validation-modify'), (value) => value?.toLowerCase() !== 'default')
              .test('unique-name', t('view-validation-exists'), (value) => {
                const existingNames = viewConfigs?.map((view) => view.name.toLowerCase()) ?? [];
                return value ? !existingNames.includes(value.toLowerCase()) : true;
              }),
          otherwise: (schema) => schema.notRequired(),
        }),
        selectedViewToUpdate: yup.object().when('saveOption', {
          is: (val: RadioGroupResult) => val.value === SAVE_EXISTING_VIEW,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [viewConfigs, t]
  );

  const createUpdateOptions = getCreateUpdateOptions();
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'all',
    defaultValues: {
      saveOption: createUpdateOptions[0],
      globalView: { checked: false },
      newViewName: '',
    } as FieldValues,
  });

  useEffect(() => {
    if (visible) {
      reset();
      const currentView = availableViews?.find((view) => view.id === viewConfig?.value);
      if (currentView) {
        setValue('selectedViewToUpdate', { text: currentView.name, value: currentView.id });
      }
    }
  }, [visible, availableViews]);

  const actionValue = watch('saveOption');
  const selectedViewToUpdate = watch('selectedViewToUpdate');

  useEffect(() => {
    let isGlobalView = false;
    if (actionValue.value === CREATE_NEW_VIEW) {
      inputRef.current?.focus();
    } else if (actionValue.value === SAVE_EXISTING_VIEW) {
      const currentView = viewConfigs?.find((view) => view.id === selectedViewToUpdate?.value);
      isGlobalView = currentView?.global ?? false;
    }
    // Keep original value to avoid overriding when user doesn't have access to the checkbox
    setValue('globalView', {
      checked: isGlobalView,
      name: 'globalView',
      value: isGlobalView,
    });
  }, [actionValue, canManageGlobalViews, selectedViewToUpdate, viewConfigs, setValue]);

  const onFormSubmit = async (data: FieldValues) => {
    const isGlobal = data.globalView?.checked ?? false;
    try {
      if (actionValue.value === CREATE_NEW_VIEW) {
        const newViewName = data.newViewName as string;
        const configuration = getViewConfiguration(undefined, newViewName, isGlobal);
        await addViewConfig(configuration);
      } else if (actionValue.value === SAVE_EXISTING_VIEW) {
        const selectedViewToUpdate = data.selectedViewToUpdate as DropDownResult;
        const viewId = selectedViewToUpdate.value as number;
        const configuration = getViewConfiguration(viewId, selectedViewToUpdate.text, isGlobal);
        await updateViewConfig({ id: configuration.id ?? 0, ...configuration });
      }
    } catch (_error) {
      showError(t('error-saving-view', 'An error occurred while saving the view.'));
    } finally {
      onCloseModal();
    }
  };

  useEffect(() => {
    setSaveButton((current) => ({ ...current, disabled: !isValid || isSubmitting }));
  }, [isValid, isSubmitting]);

  const handleInputRef = (value: unknown) => {
    inputRef.current = forceAssert<HTMLInputElement>(value);
  };

  return (
    <Dialog
      loading={isViewConfigsLoading}
      visible={visible}
      onClose={onCloseModal}
      header={t('view')}
      buttonType="submit"
      buttons={[saveButton]}
    >
      <form onSubmit={handleSubmit(onFormSubmit)} id={FORM_ID} className="flex flex-col gap-2">
        <HfRadioGroup
          control={control}
          name="saveOption"
          id="saveOption"
          textField="text"
          valueField="value"
          data={createUpdateOptions}
          orientation="HORIZONTAL"
          required={DynamicFormRequiredType.True}
          readOnly={false}
        />
        {actionValue.value === SAVE_EXISTING_VIEW && (
          <HfDropDown
            name="selectedViewToUpdate"
            control={control}
            textField="name"
            valueField="id"
            required={DynamicFormRequiredType.True}
            data={availableViews as unknown as JsonData}
            allowClear={false}
          />
        )}
        {actionValue.value === CREATE_NEW_VIEW && (
          <HfInput
            control={control}
            name="newViewName"
            required={DynamicFormRequiredType.True}
            placeholder={t('enter-name')}
            inputRef={handleInputRef}
          />
        )}
        {canManageGlobalViews && <HfCheckbox control={control} name="globalView" label={t('global')} />}
      </form>
    </Dialog>
  );
}
